import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Input, message, Popconfirm, Typography, Drawer } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { IPropertySimulation, IPropertySale } from 'types/PropertyTypes';
import PropertySaleDetailsContent from '../../components/PropertySaleDetailsContent';
import { calcSimulation, calcActualYield, calcBuildingAge } from 'utils/simulationCalc';
import * as simulationApi from '../../services/simulationApi';
import * as propertySaleHistoryApi from '../../services/propertySaleHistoryApi';
import type { ColumnsType } from 'antd/es/table';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const BLUE_HEAD = { style: { background: '#F9FAFB', color: '#6B7280', fontWeight: 600 as const, whiteSpace: 'nowrap' as const, borderBottom: '1px solid #E5E7EB' as const } };
const RED_HEAD  = { style: { background: '#FFF5F5', color: '#DC2626', fontWeight: 600 as const, whiteSpace: 'nowrap' as const, borderBottom: '1px solid #FECACA' as const } };
const GREEN_HEAD = { style: { background: '#F0FDF4', color: '#16A34A', fontWeight: 600 as const, whiteSpace: 'nowrap' as const, borderBottom: '1px solid #BBF7D0' as const } };
const BLUE_CELL = { style: { background: '#FFFFFF', color: '#374151' } };
const RED_CELL  = { style: { background: '#FFFBFB', color: '#374151', cursor: 'pointer' as const } };
const GREEN_CELL = { style: { background: '#FAFFF7', color: '#374151' } };

interface EditingCell {
  id: number;
  field: 'estimated_rent' | 'target_yield' | 'price_total' | 'management_fee' | 'reserve_fund' | 'other_cost';
}

const SimulationPage: React.FC = () => {
  const [simulations, setSimulations] = useState<IPropertySimulation[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const inputRef = useRef<any>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // Drawer state for property details
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<IPropertySale | undefined>(undefined);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedSimulationId, setSelectedSimulationId] = useState<number | null>(null);

  const loadSimulations = async (page: number = currentPage, size: number = pageSize) => {
    setLoading(true);
    try {
      const result = await simulationApi.getList(page, size);
      setSimulations(result.data);
      setTotal(result.total);
      setCurrentPage(result.current_page);
    } catch {
      message.error('データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSimulations(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCellClick = (id: number, field: EditingCell['field'], currentValue: number | null) => {
    setEditingCell({ id, field });
    setEditingValue(currentValue != null ? String(currentValue) : '');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleEditConfirm = async () => {
    if (!editingCell) {
      setEditingCell(null);
      return;
    }
    const isIntField = editingCell.field === 'price_total' || editingCell.field === 'management_fee' || editingCell.field === 'reserve_fund' || editingCell.field === 'other_cost';
    const numVal = editingValue === '' ? null : (isIntField ? parseInt(editingValue, 10) : parseFloat(editingValue));
    if (editingValue !== '' && (isNaN(numVal!) || numVal! < 0)) {
      message.error('正の数値を入力してください');
      return;
    }
    try {
      const updated = await simulationApi.update(editingCell.id, { [editingCell.field]: numVal } as Partial<IPropertySimulation>);
      setSimulations(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch {
      message.error('保存に失敗しました');
    } finally {
      setEditingCell(null);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await simulationApi.del(id);
      message.success('削除しました');
      await loadSimulations(currentPage, pageSize);
    } catch {
      message.error('削除に失敗しました');
    }
  };

  /**
   * 物件番号から詳細情報を取得する
   */
  const fetchPropertyDetails = async (propertyNumber: string) => {
    try {
      setDetailLoading(true);
      const data = await propertySaleHistoryApi.getByPropertyNumber(propertyNumber);
      setSelectedProperty(data);
    } catch {
      message.error('詳細情報の読み込みに失敗しました');
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * 物件名クリック時にDrawerを開く
   */
  const handleDetailClick = (propertyNumber: string, simulationId: number) => {
    setSelectedSimulationId(simulationId);
    setDrawerVisible(true);
    fetchPropertyDetails(propertyNumber);
  };

  /**
   * Drawerを閉じる
   */
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedProperty(undefined);
    setSelectedSimulationId(null);
  };

  const handleReasonsUpdate = async (simId: number, goodReason: string, badReason: string) => {
    const updated = await simulationApi.update(simId, { good_reason: goodReason, bad_reason: badReason } as Partial<IPropertySimulation>);
    setSimulations(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  const renderEditableCell = (
    sim: IPropertySimulation,
    field: EditingCell['field'],
    suffix: string,
  ) => {
    const isEditing = editingCell?.id === sim.id && editingCell?.field === field;
    const value = sim[field];

    if (isEditing) {
      return (
        <Input
          ref={inputRef}
          value={editingValue}
          onChange={e => setEditingValue(e.target.value)}
          onBlur={handleEditConfirm}
          onPressEnter={handleEditConfirm}
          style={{ width: 90, background: '#FFF5F5', color: '#DC2626', border: '1px solid #FECACA' }}
          size="small"
        />
      );
    }

    return (
      <span
        onClick={() => handleCellClick(sim.id, field, value)}
        style={{
          display: 'inline-block',
          minWidth: 70,
          padding: '2px 4px',
          borderBottom: '1px dashed #FECACA',
          cursor: 'pointer',
        }}
      >
        {value != null && !isNaN(Number(value))
          ? `${Number(value).toLocaleString()}${suffix}`
          : <span style={{ color: '#9CA3AF', fontSize: 11 }}>入力</span>
        }
      </span>
    );
  };

  const columns: ColumnsType<IPropertySimulation> = [
    {
      title: '#',
      key: 'index',
      width: 48,
      render: (_, __, idx) => idx + 1,
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => ({ style: { ...BLUE_CELL.style, textAlign: 'center' as const } }),
    },
    {
      title: '初回確認日',
      key: 'confirmed_at',
      width: 100,
      render: (_, sim) => formatDate(sim.created_at),
      sorter: (a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '物件名',
      key: 'building_name',
      width: 180,
      ellipsis: true,
      sorter: (a, b) => (a.property_sale_history?.building_name ?? '').localeCompare(b.property_sale_history?.building_name ?? ''),
      render: (_, sim) => {
        const name = sim.property_sale_history?.building_name;
        const propNum = sim.property_sale_history?.property_number;
        if (!propNum) return name || '-';
        return (
          <span
            onClick={() => handleDetailClick(propNum, sim.id)}
            style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0EA5E9' }}
          >
            {name || '(物件名なし)'}
          </span>
        );
      },
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '住所',
      key: 'address',
      width: 160,
      ellipsis: true,
      sorter: (a, b) => {
        const addrA = [a.property_sale_history?.address_1, a.property_sale_history?.address_2].filter(Boolean).join('');
        const addrB = [b.property_sale_history?.address_1, b.property_sale_history?.address_2].filter(Boolean).join('');
        return addrA.localeCompare(addrB);
      },
      render: (_, sim) => {
        const p = sim.property_sale_history;
        if (!p) return '-';
        return [p.address_1, p.address_2, p.address_3].filter(Boolean).join('') || '-';
      },
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '最寄り駅',
      key: 'station',
      width: 120,
      render: (_, sim) => {
        const p = sim.property_sale_history;
        if (!p) return '-';
        const parts = [p.line_1, p.station_1 ? p.station_1 + '駅' : ''].filter(Boolean);
        return parts.join(' ') || '-';
      },
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '駅徒歩',
      key: 'walk_minutes',
      width: 80,
      render: (_, sim) => {
        const w = sim.property_sale_history?.walk_minutes_1;
        return w ? `${w}分` : '-';
      },
      sorter: (a, b) => Number(a.property_sale_history?.walk_minutes_1 ?? 9999) - Number(b.property_sale_history?.walk_minutes_1 ?? 9999),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '築年数',
      key: 'built_year',
      width: 80,
      sorter: (a, b) => {
        const pA = a.property_sale_history as any;
        const pB = b.property_sale_history as any;
        const ageA = calcBuildingAge(String(pA?.built_year || pA?.built_year_month || '')) ?? 9999;
        const ageB = calcBuildingAge(String(pB?.built_year || pB?.built_year_month || '')) ?? 9999;
        return ageA - ageB;
      },
      render: (_, sim) => {
        const p = sim.property_sale_history as any;
        const raw = p?.built_year || p?.built_year_month;
        const age = calcBuildingAge(raw != null ? String(raw) : null);
        return age !== null ? `${age}年` : '-';
      },
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '平米数',
      key: 'area_m2',
      width: 90,
      render: (_, sim) => {
        const v = sim.property_sale_history?.area_m2;
        return v != null && v !== '' ? `${v}㎡` : '-';
      },
      sorter: (a, b) => Number(a.property_sale_history?.area_m2 ?? 0) - Number(b.property_sale_history?.area_m2 ?? 0),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '販売価格',
      key: 'price_total',
      width: 100,
      render: (_, sim) => renderEditableCell(sim, 'price_total', '万'),
      sorter: (a, b) => (a.price_total ?? 0) - (b.price_total ?? 0),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => ({ style: { ...BLUE_CELL.style, cursor: 'pointer' as const } }),
    },
    {
      title: '坪単価',
      key: 'price_per_tsubo',
      width: 100,
      render: (_, sim) => {
        const v = sim.property_sale_history?.price_per_tsubo;
        return v != null && v !== 0 ? `${Number(v).toLocaleString()}万` : '-';
      },
      sorter: (a, b) => Number(a.property_sale_history?.price_per_tsubo ?? 0) - Number(b.property_sale_history?.price_per_tsubo ?? 0),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '㎡単価',
      key: 'price_per_m2',
      width: 100,
      render: (_, sim) => {
        const v = sim.property_sale_history?.price_per_m2;
        return v != null && v !== 0 ? `${Number(v).toLocaleString()}万` : '-';
      },
      sorter: (a, b) => Number(a.property_sale_history?.price_per_m2 ?? 0) - Number(b.property_sale_history?.price_per_m2 ?? 0),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '修繕積立金',
      key: 'reserve_fund',
      width: 100,
      render: (_, sim) => renderEditableCell(sim, 'reserve_fund', '円'),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => ({ style: { ...BLUE_CELL.style, cursor: 'pointer' as const } }),
    },
    {
      title: '管理費',
      key: 'management_fee',
      width: 90,
      render: (_, sim) => renderEditableCell(sim, 'management_fee', '円'),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => ({ style: { ...BLUE_CELL.style, cursor: 'pointer' as const } }),
    },
    {
      title: 'その他',
      key: 'other_cost',
      width: 90,
      render: (_, sim) => renderEditableCell(sim, 'other_cost', '円'),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => ({ style: { ...BLUE_CELL.style, cursor: 'pointer' as const } }),
    },
    {
      title: '年間管理費\n修繕積立金',
      key: 'annual_running_cost',
      width: 140,
      render: (_, sim) => {
        const mFee = sim.management_fee;
        const rFund = sim.reserve_fund;
        const oCost = sim.other_cost;
        if (mFee == null && rFund == null && oCost == null) return '-';
        const annual = (Number(mFee || 0) + Number(rFund || 0) + Number(oCost || 0)) * 12;
        return isNaN(annual) ? '-' : `${annual.toLocaleString()}円`;
      },
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
    {
      title: '楽待利回り🔴\n(%)',
      key: 'target_yield',
      width: 120,
      render: (_, sim) => renderEditableCell(sim, 'target_yield', '%'),
      sorter: (a, b) => Number(a.target_yield ?? 0) - Number(b.target_yield ?? 0),
      onHeaderCell: () => RED_HEAD,
      onCell: () => RED_CELL,
    },
    {
      title: '想定家賃🔴\n(万/月)',
      key: 'estimated_rent',
      width: 130,
      render: (_, sim) => renderEditableCell(sim, 'estimated_rent', '万'),
      sorter: (a, b) => Number(a.estimated_rent ?? 0) - Number(b.estimated_rent ?? 0),
      onHeaderCell: () => RED_HEAD,
      onCell: () => RED_CELL,
    },
    {
      title: '想定年間賃料🟢',
      key: 'annual_rent',
      width: 130,
      sorter: (a, b) => {
        const calcA = calcSimulation(Number(a.estimated_rent) || null, Number(a.target_yield) || null, a.price_total);
        const calcB = calcSimulation(Number(b.estimated_rent) || null, Number(b.target_yield) || null, b.price_total);
        return (calcA?.annualRent ?? 0) - (calcB?.annualRent ?? 0);
      },
      render: (_, sim) => {
        const calc = calcSimulation(
          sim.estimated_rent != null ? Number(sim.estimated_rent) : null,
          sim.target_yield != null ? Number(sim.target_yield) : null,
          sim.price_total,
        );
        return calc ? `${calc.annualRent.toLocaleString('ja-JP', { maximumFractionDigits: 1 })}万` : '-';
      },
      onHeaderCell: () => GREEN_HEAD,
      onCell: () => GREEN_CELL,
    },
    {
      title: '想定売却費🟢',
      key: 'sale_price',
      width: 120,
      sorter: (a, b) => {
        const calcA = calcSimulation(Number(a.estimated_rent) || null, Number(a.target_yield) || null, a.price_total);
        const calcB = calcSimulation(Number(b.estimated_rent) || null, Number(b.target_yield) || null, b.price_total);
        return (calcA?.salePrice ?? 0) - (calcB?.salePrice ?? 0);
      },
      render: (_, sim) => {
        const calc = calcSimulation(
          sim.estimated_rent != null ? Number(sim.estimated_rent) : null,
          sim.target_yield != null ? Number(sim.target_yield) : null,
          sim.price_total,
        );
        return calc ? `${calc.salePrice.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}万` : '-';
      },
      onHeaderCell: () => GREEN_HEAD,
      onCell: () => GREEN_CELL,
    },
    {
      title: '想定諸経費🟢',
      key: 'expenses',
      width: 120,
      sorter: (a, b) => {
        const calcA = calcSimulation(Number(a.estimated_rent) || null, Number(a.target_yield) || null, a.price_total);
        const calcB = calcSimulation(Number(b.estimated_rent) || null, Number(b.target_yield) || null, b.price_total);
        return (calcA?.expenses ?? 0) - (calcB?.expenses ?? 0);
      },
      render: (_, sim) => {
        const calc = calcSimulation(
          sim.estimated_rent != null ? Number(sim.estimated_rent) : null,
          sim.target_yield != null ? Number(sim.target_yield) : null,
          sim.price_total,
        );
        return calc ? `${calc.expenses.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}万` : '-';
      },
      onHeaderCell: () => GREEN_HEAD,
      onCell: () => GREEN_CELL,
    },
    {
      title: '想定利益🟢',
      key: 'profit',
      width: 120,
      sorter: (a, b) => {
        const calcA = calcSimulation(Number(a.estimated_rent) || null, Number(a.target_yield) || null, a.price_total);
        const calcB = calcSimulation(Number(b.estimated_rent) || null, Number(b.target_yield) || null, b.price_total);
        return (calcA?.profit ?? -Infinity) - (calcB?.profit ?? -Infinity);
      },
      render: (_, sim) => {
        const calc = calcSimulation(
          sim.estimated_rent != null ? Number(sim.estimated_rent) : null,
          sim.target_yield != null ? Number(sim.target_yield) : null,
          sim.price_total,
        );
        if (!calc) return '-';
        const color = calc.profit >= 0 ? '#16A34A' : '#DC2626';
        return (
          <span style={{ color }}>
            {calc.profit.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}万
          </span>
        );
      },
      onHeaderCell: () => GREEN_HEAD,
      onCell: () => GREEN_CELL,
    },
    {
      title: '想定利益率🟢',
      key: 'profit_rate',
      width: 90,
      render: () => '-',
      onHeaderCell: () => GREEN_HEAD,
      onCell: () => GREEN_CELL,
    },
    {
      title: '実際利回り🟢',
      key: 'actual_yield',
      width: 110,
      sorter: (a, b) => {
        const yA = calcActualYield(Number(a.estimated_rent) || null, a.price_total, a.management_fee, a.reserve_fund, a.other_cost) ?? -Infinity;
        const yB = calcActualYield(Number(b.estimated_rent) || null, b.price_total, b.management_fee, b.reserve_fund, b.other_cost) ?? -Infinity;
        return yA - yB;
      },
      render: (_, sim) => {
        const actualYield = calcActualYield(
          sim.estimated_rent != null ? Number(sim.estimated_rent) : null,
          sim.price_total,
          sim.management_fee,
          sim.reserve_fund,
          sim.other_cost,
        );
        if (actualYield === null || isNaN(actualYield)) return '-';
        const color = actualYield >= 0 ? '#16A34A' : '#DC2626';
        return <span style={{ color }}>{actualYield.toFixed(2)}%</span>;
      },
      onHeaderCell: () => GREEN_HEAD,
      onCell: () => GREEN_CELL,
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (_, sim) => (
        <Popconfirm
          title="削除しますか？"
          onConfirm={() => handleDelete(sim.id)}
          okText="削除"
          cancelText="キャンセル"
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
          >
            削除
          </Button>
        </Popconfirm>
      ),
      onHeaderCell: () => BLUE_HEAD,
      onCell: () => BLUE_CELL,
    },
  ];

  return (
    <div className="dark-theme-container">
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ color: '#111827', margin: 0 }}>
          シミュレーション一覧
        </Typography.Title>
        <Typography.Text style={{ color: '#9CA3AF', fontSize: 12 }}>
          物件一覧の「査定」ボタンで追加。赤セルをクリックして査定家賃・表面利回りを入力すると緑欄が自動計算されます。
        </Typography.Text>
      </div>

      <Table<IPropertySimulation>
        dataSource={simulations.map(s => ({ ...s, key: s.id }))}
        columns={columns}
        loading={loading}
        scroll={{ x: 2500 }}
        onChange={() => {}}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (t) => `計 ${t} 件`,
          style: { color: '#374151' },
          onChange: (page, size) => {
            setPageSize(size);
            loadSimulations(page, size);
          },
        }}
        size="small"
        style={{ background: '#FFFFFF' }}
      />

      <Drawer
        title="物件詳細"
        placement="right"
        onClose={handleDrawerClose}
        open={drawerVisible}
        width="44%"
        styles={{
          header: {
            background: '#FFFFFF',
            borderBottom: '1px solid #E5E7EB',
          },
          body: {
            background: '#FFFFFF',
            paddingTop: 24,
          },
        }}
      >
        <PropertySaleDetailsContent
          propertySale={selectedProperty}
          loading={detailLoading}
          goodReason={simulations.find(s => s.id === selectedSimulationId)?.good_reason}
          badReason={simulations.find(s => s.id === selectedSimulationId)?.bad_reason}
          simulationId={selectedSimulationId}
          onReasonsUpdate={handleReasonsUpdate}
        />
      </Drawer>
    </div>
  );
};

export default SimulationPage;
